import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { PrestadoresAcreditados27Service } from './prestadores-acreditados-2-7.service';
import { RelatorioPrestadoresAcreditados27 } from '@gestor-web/@core/api/ords/model/relatorio-prestadores-acreditados-2-7';
import { SelectItem } from 'primeng/api';
import { PrestadoresAcreditados27FilterData } from './components/filtro/filtro.component';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'PRESTADORES_ACREDITADOS_2_7';

const simNao = (params: any) => (params.value === 1 || params.value === '1' ? 'Sim' : 'Não');

const SIM_NAO_OPTIONS: SelectItem[] = [
  { label: 'Sim', value: 1 },
  { label: 'Não', value: 0 },
];

const COLUMNS: ColDef[] = [
  {
    headerName: '014-CPF/CNPJ executante',
    field: 'nr_cnpj_cpf',
    type: 'cpfCnpj',
    width: 210,
  },
  {
    headerName: 'Executante',
    field: 'nm_fantasia',
    width: 220,
  },
  {
    headerName: 'Nr. CNES',
    field: 'lt_nr_cnes',
    width: 130,
  },
  {
    headerName: 'Tipo estabelecimento',
    field: 'ds_tipo_estabelecimento',
    width: 220,
  },
  {
    headerName: 'Operadora intermediária',
    field: 'ds_intermediaria',
    width: 200,
  },
  {
    headerName: 'CNES acreditado',
    field: 'nr_cnes_acreditado',
    width: 160,
  },
  {
    headerName: 'Acreditação',
    field: 'sg_acreditacao',
    width: 150,
  },
  {
    headerName: '180+ dias acreditado?',
    field: 'ib_qt_dia',
    width: 190,
    valueFormatter: simNao,
  },
  {
    headerName: 'Está no cadastro do RPS?',
    field: 'ib_rps',
    width: 200,
    valueFormatter: simNao,
  },
  {
    headerName: 'Qtde. guia incorporada',
    field: 'qt_guia_incorporada',
    type: 'numericColumn',
    width: 220,
    sort: 'desc',
    sortIndex: 0,
  },
  {
    headerName: 'Qtde. guia rejeitada',
    field: 'qt_guia_rejeitada',
    type: 'numericColumn',
    width: 190,
  },
];

@Component({
  selector: 'bl-prestadores-acreditados-2-7',
  templateUrl: './prestadores-acreditados-2-7.component.html',
  styleUrls: ['./prestadores-acreditados-2-7.component.scss'],
  providers: [PrestadoresAcreditados27Service, DatePipe],
  standalone: false,
})
export class PrestadoresAcreditados27Component implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<RelatorioPrestadoresAcreditados27>;

  gridOptions: GridOptions<RelatorioPrestadoresAcreditados27>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  competenciaRange: string[];
  rowData: RelatorioPrestadoresAcreditados27[];

  pageTooltip = 'RELATORIOS_PERSONALIZADOS.PRESTADORES_ACREDITADOS_2_7';

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  operadora: string;
  operadoraNome: string;
  podeGerenciarVisoes = false;

  dtUltimoProcessamento: string = null;

  filterData: PrestadoresAcreditados27FilterData = {
    filAcreditado: null,
    fil180Dias: null,
    filRps: null,
  };

  constructor(
    private permissaoService: PermissaoService,
    private service: PrestadoresAcreditados27Service,
    private notificationService: NotificationService,
    private gridService: GridService,
    private competenciaPipe: CompetenciaPipe,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen).pipe(takeUntil(this.ngDestroyed$));
    this.gridHeight$ = of('100%');

    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : this.competenciaRange[0],
          params.competencia_final ? '' + params.competencia_final : this.competenciaRange[1],
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions({
      columnDefs: COLUMNS,
      pipes: {
        decimal: this.decimalPipe,
        competencia: this.competenciaPipe,
        cpfCnpj: this.cpfCnpjPipe,
      },
      views: {
        gridKey: GRID_KEY,
        hide: !this.podeGerenciarVisoes,
      },
    });
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, fil_acreditado, fil_180_dias, fil_rps, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    const filterData = { ...this.filterData };

    if (fil_acreditado !== undefined) {
      hasFilter = true;
      filterData.filAcreditado = this.toOpcaoUnica(fil_acreditado, SIM_NAO_OPTIONS);
    }

    if (fil_180_dias !== undefined) {
      hasFilter = true;
      filterData.fil180Dias = this.toOpcaoUnica(fil_180_dias, SIM_NAO_OPTIONS);
    }

    if (fil_rps !== undefined) {
      hasFilter = true;
      filterData.filRps = this.toOpcaoUnica(fil_rps, SIM_NAO_OPTIONS);
    }

    this.filterData = filterData;

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], { relativeTo: this.route, queryParams: {} });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.dtUltimoProcessamento = null;
      this.agGrid?.api?.setGridOption('loading', false);
      return;
    }

    const grid$ = this.service.fetchData(this.operadora, this.competenciaRange[0], this.competenciaRange[1], {
      filAcreditado: this.filterData.filAcreditado ?? undefined,
      fil180Dias: this.filterData.fil180Dias ?? undefined,
      filRps: this.filterData.filRps ?? undefined,
    });

    const ultimo$ = this.service.getUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({ grid: grid$, ultimo: ultimo$ }).subscribe({
      next: ({ grid, ultimo }) => {
        this.rowData = grid;
        this.dtUltimoProcessamento = this.getDataUltimoProcessamento(ultimo);
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  private getDataUltimoProcessamento(ultimoProcessamentoResponse: {
    items?: Array<{ dt_atualizacao_mon_guia?: string | number }>;
  }): string {
    const valor = ultimoProcessamentoResponse?.items?.[0]?.dt_atualizacao_mon_guia;
    if (!valor) {
      return null;
    }

    const timestamp = Number(valor);
    return this.datePipe.transform(Number.isNaN(timestamp) ? valor : timestamp, 'dd/MM/yyyy');
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
  }

  onSearch(value: string) {
    this.agGrid.api.updateGridOptions({ quickFilterText: value });
  }

  onFiltroChange(data: PrestadoresAcreditados27FilterData): void {
    this.filterData = data;
    this.filterChanged = true;
  }

  private toOpcaoUnica(valor: unknown, options: SelectItem[]): number {
    const numero = Number(valor);
    return options.some(option => Number(option.value) === numero) ? numero : null;
  }

  exportExcel(): void {
    const fileNamePrefix = `Relatorio-Prestadores-Acreditados-2.7-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;
    const headers = [
      'Relatório de prestadores acreditados - 2.7',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];
    exportGridAsExcel({ api: this.agGrid.api, headers, fileNamePrefix });
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      fil_acreditado: this.filterData.filAcreditado,
      fil_180_dias: this.filterData.fil180Dias,
      fil_rps: this.filterData.filRps,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
